import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import { SITE_LOGO_URL } from '../../lib/brand';
import { avatarPublicUrl } from '../../lib/memberHelpers';

const NavLinkDesktop = ({ to, icon, label, active }) => (
  <Link 
    to={to} 
    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all font-medium ${active ? 'bg-[#74ACDF] text-white shadow-md shadow-[#74ACDF]/20' : 'text-[#1B2A4E] hover:bg-stone-100'}`}
  >
    <SafeIcon name={icon} className="w-4.5 h-4.5" />
    <span className="text-sm">{label}</span>
  </Link>
);

const NavLinkMobile = ({ to, icon, label, active }) => (
  <Link 
    to={to} 
    className={`flex flex-col items-center justify-center w-full py-3 transition-colors ${active ? 'text-[#74ACDF]' : 'text-stone-400'}`}
  >
    <SafeIcon name={icon} className="w-6 h-6 mb-1" />
    <span className="label-technical text-[9px]">{label}</span>
  </Link>
);

export default function Layout({ children }) {
  const { currentUser, isAuthenticated } = useApp();
  const location = useLocation();

  // If not authenticated OR on an invite link, we just render the content wrapper
  // (Landing handles its own simple header; public /negocio/:slug also works unauthenticated)
  const isInviteRoute = location.pathname.startsWith('/invitacion');
  if (!isAuthenticated || isInviteRoute) {
    return <div className="min-h-screen bg-[#FAF9F6]">{children}</div>;
  }

  const isAdminOrMod = ['admin', 'moderador'].includes(currentUser?.role);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col pb-20 md:pb-0 sol-watermark">
      
      {/* Top Background Band (Navy) */}
      <div className="hidden md:block h-1 bg-[#1B2A4E] w-full sticky top-[72px] z-40"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200 px-6 py-3 items-center justify-between flex h-[72px]">
        <div className="flex items-center gap-10">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={SITE_LOGO_URL}
              alt="Argentina Emprende"
              className="w-10 h-10 rounded-xl object-cover shadow-lg shrink-0"
            />
            <div className="hidden lg:block">
              <span className="font-editorial font-bold text-lg text-[#1B2A4E] leading-none block">Argentina</span>
              <span className="label-technical text-[#74ACDF] text-[9px]">Emprende</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLinkDesktop to="/" icon="Home" label="Inicio" active={location.pathname === '/'} />
            <NavLinkDesktop to="/directorio" icon="Users" label="Directorio" active={location.pathname.startsWith('/directorio')} />
            <NavLinkDesktop to="/academia" icon="BookOpen" label="Academia" active={location.pathname.startsWith('/academia')} />
            <NavLinkDesktop to="/clubs" icon="MessageSquare" label="Clubs" active={location.pathname.startsWith('/clubs')} />
            <NavLinkDesktop to="/eventos" icon="Calendar" label="Eventos" active={location.pathname.startsWith('/eventos')} />
            {isAdminOrMod && (
              <NavLinkDesktop to="/admin" icon="Shield" label="Admin" active={location.pathname.startsWith('/admin')} />
            )}
          </nav>
        </div>

        <Link to="/perfil" className="flex items-center gap-3 group">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-bold text-[#1B2A4E] group-hover:text-[#74ACDF] transition-colors">{currentUser?.nombre}</p>
            <p className="label-technical text-stone-400 text-[9px]">{currentUser?.role}</p>
          </div>
          <div className="relative">
            <img
              src={
                avatarPublicUrl(currentUser?.foto_url) ||
                `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(currentUser?.nombre || '?')}`
              }
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm ring-2 ring-stone-100"
            />
            <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
          </div>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-6xl mx-auto p-4 md:p-10 relative z-10">
        {children}
      </main>

      {/* Mobile Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-stone-200 flex items-center justify-around z-50 pb-safe overflow-x-auto no-scrollbar px-2">
        <NavLinkMobile to="/" icon="Home" label="Inicio" active={location.pathname === '/'} />
        <NavLinkMobile to="/directorio" icon="Users" label="Directorio" active={location.pathname.startsWith('/directorio')} />
        <NavLinkMobile to="/academia" icon="BookOpen" label="Academia" active={location.pathname.startsWith('/academia')} />
        <NavLinkMobile to="/clubs" icon="MessageSquare" label="Clubs" active={location.pathname.startsWith('/clubs')} />
        <NavLinkMobile to="/eventos" icon="Calendar" label="Eventos" active={location.pathname.startsWith('/eventos')} />
      </nav>
    </div>
  );
}